import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PlusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import type { IDomEditor, IEditorConfig, IToolbarConfig } from '@wangeditor/editor';
import { Editor, Toolbar } from '@wangeditor/editor-for-react';
import {
  Button,
  DatePicker,
  Divider,
  Drawer,
  Form,
  Image,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useSnapshot } from 'valtio';
import {
  addExchangeProductApi,
  deleteExchangeProductApi,
  ExchangeProductItem,
  getExchangeProductDetailApi,
  getExchangeProductPageApi,
  getStockStatusEnumApi,
  increaseExchangeProductStockApi,
  reduceExchangeProductStockApi,
  StockStatusEnumItem,
  updateExchangeProductApi,
} from '@/api/pointCenter/exchangeProduct';
import { state } from '@/store';
import '@wangeditor/editor/dist/css/style.css';
import ImageUploader from '@/component/ImageUploader';
import { PHYSICAL_ASSET_KEY } from '@/router/Permissions';
import { defaultEditorConfig, defaultToolbarConfig } from '@/utils/editorConfig';
import { checkPermissions } from '@/utils/utils';

const { RangePicker } = DatePicker;
const { Link, Text } = Typography;
type DrawerMode = 'create' | 'edit' | 'copy';
const EMPTY_HTML = '<p><br></p>';
const isEmptyHtml = (html: string) => {
  if (!html) return true;
  if (/<img\b[^>]*\bsrc\s*=\s*['"][^'"]+['"][^>]*>/i.test(html)) {
    return false;
  }
  const plain = html
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, '')
    .trim();
  return !plain;
};

const AssetManageGoods: React.FC = () => {
  const [form] = Form.useForm();
  const [drawerForm] = Form.useForm();
  const { currentShopId } = useSnapshot(state);
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<ExchangeProductItem[]>([]);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [stockStatusOptions, setStockStatusOptions] = useState<{ label: string; value: string }[]>([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerMode, setDrawerMode] = useState<DrawerMode>('create');
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editor, setEditor] = useState<IDomEditor | null>(null);
  const [editorHtml, setEditorHtml] = useState<string>(EMPTY_HTML);
  const [stockModalVisible, setStockModalVisible] = useState(false);
  const [stockAdjustForm] = Form.useForm();
  const [stockTarget, setStockTarget] = useState<any>(null);

  const toolbarConfig = useMemo<Partial<IToolbarConfig>>(() => defaultToolbarConfig, []);

  const editorConfig = useMemo<Partial<IEditorConfig>>(() => defaultEditorConfig, []);

  useEffect(() => {
    fetchStockStatus();
  }, []);

  useEffect(() => {
    fetchList({ pageNum: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentShopId]);

  useEffect(() => {
    if (!drawerVisible && editor) {
      editor.destroy();
      setEditor(null);
    }
  }, [drawerVisible, editor]);

  useEffect(() => {
    return () => {
      if (editor) {
        editor.destroy();
      }
    };
  }, [editor]);

  const fetchStockStatus = async () => {
    try {
      const res = await getStockStatusEnumApi();
      const list: StockStatusEnumItem[] = Array.isArray(res?.data) ? res.data : [];
      if (res?.code === 200) {
        setStockStatusOptions(list.map(item => ({ label: item.description || item.code, value: item.code })));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const openDrawer = useCallback(
    async (mode: DrawerMode, record?: ExchangeProductItem) => {
      if (!currentShopId) {
        message.warning('请先选择店铺');
        return;
      }
      setDrawerMode(mode);
      setDrawerVisible(true);
      setEditorHtml(EMPTY_HTML);
      if (editor) {
        editor.destroy();
        setEditor(null);
      }
      drawerForm.resetFields();
      setCurrentId(record?.id ?? null);
      if (mode === 'create') {
        drawerForm.setFieldsValue({
          carouselImages: [''],
          status: 1,
          productDetails: EMPTY_HTML,
          stockUnit: '件',
        });
        return;
      }
      if (!record?.id) return;
      setDetailLoading(true);
      try {
        const res = await getExchangeProductDetailApi(currentShopId, record.id);
        const detail: ExchangeProductItem | undefined = res?.data;
        if (res?.code === 200 && detail) {
          const detailHtml = detail.details?.productDetails || detail.productDetails || EMPTY_HTML;
          drawerForm.setFieldsValue({
            productName: detail.productName,
            barcode: detail.barcode,
            productIntro: detail.productIntro,
            originalPrice: detail.originalPrice,
            productStock: detail.productStock,
            safeStock: detail.safeStock,
            thumbImage: detail.thumbImage,
            carouselImages: detail.carouselImages && detail.carouselImages.length ? detail.carouselImages : [''],
            status: detail.status ?? 1,
            stockUnit: detail.stockUnit || '件',
            productDetails: detailHtml,
          });
          setEditorHtml(detailHtml);
          if (mode === 'copy') {
            setDrawerMode('create');
            setCurrentId(null);
          }
        } else {
          setDrawerVisible(false);
        }
      } catch (error) {
        console.error(error);
        setDrawerVisible(false);
      } finally {
        setDetailLoading(false);
      }
    },
    [currentShopId, drawerForm],
  );

  const handleDelete = (record: ExchangeProductItem) => {
    if (!currentShopId) {
      message.warning('请先选择店铺');
      return;
    }
    Modal.confirm({
      title: '确认删除该实物资产？',
      okText: '删除',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const res = await deleteExchangeProductApi(currentShopId, record.id);
          if (res?.code === 200) {
            message.success('删除成功');
            fetchList({ pageNum: 1 });
          }
        } catch (error) {
          console.error(error);
        }
      },
    });
  };

  const openStockModal = async (record: ExchangeProductItem) => {
    if (!currentShopId) {
      message.warning('请先选择店铺');
      return;
    }
    try {
      const res = await getExchangeProductDetailApi(currentShopId, record.id);
      if (res?.code === 200 && res?.data) {
        const detail = res.data;
        const totalStock = (detail.productStock ?? 0) + (detail.frozenStock ?? 0);
        const availableStock = detail.productStock ?? 0;
        setStockTarget({ ...record, ...detail, totalStock, availableStock });
        setStockModalVisible(true);
        stockAdjustForm.resetFields();
        stockAdjustForm.setFieldsValue({ mode: 'increase' });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const submitStockAdjust = async () => {
    if (!currentShopId || !stockTarget?.id) {
      message.warning('缺少店铺或商品信息');
      return;
    }
    try {
      const values = await stockAdjustForm.validateFields();
      const quantity = Number(values.quantity);
      if (Number.isNaN(quantity) || quantity <= 0) {
        message.error('请输入大于0的调整数量');
        return;
      }
      const api =
        values.mode === 'decrease'
          ? reduceExchangeProductStockApi(currentShopId, stockTarget.id, quantity, values.remark)
          : increaseExchangeProductStockApi(currentShopId, stockTarget.id, quantity, values.remark);
      const res = await api;
      if (res?.code === 200) {
        message.success('调整成功');
        setStockModalVisible(false);
        fetchList();
      }
    } catch (error: any) {
      if (error?.errorFields) return;
    }
  };

  const fetchList = async (override?: Partial<{ pageNum: number; pageSize: number }>) => {
    if (!currentShopId) {
      message.warning('请先选择店铺');
      return;
    }
    const values = form.getFieldsValue();
    const createRange: Dayjs[] | undefined = values.createTime;
    const params = {
      shopId: currentShopId,
      productName: values.productName || undefined,
      barcode: values.barcode || undefined,
      stockStatus: values.stockStatus || undefined,
      id: values.id || undefined,
      createTimeBegin: createRange?.[0] ? createRange[0].startOf('day').format('YYYY-MM-DD HH:mm:ss') : undefined,
      createTimeEnd: createRange?.[1] ? createRange[1].endOf('day').format('YYYY-MM-DD HH:mm:ss') : undefined,
      pageNum: override?.pageNum || pageNum,
      pageSize: override?.pageSize || pageSize,
    };
    setLoading(true);
    try {
      const res = await getExchangeProductPageApi(params);
      if (res?.code === 200) {
        const rows = Array.isArray(res.rows) ? res.rows : [];
        setDataSource(rows);
        setTotal(res.total || 0);
        if (override?.pageNum) setPageNum(override.pageNum);
        if (override?.pageSize) setPageSize(override.pageSize);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!currentShopId) {
      message.warning('请先选择店铺');
      return;
    }
    if (drawerMode === 'edit' && !currentId) {
      message.error('缺少ID');
      return;
    }
    try {
      drawerForm.setFieldsValue({ productDetails: editorHtml });
      const values = await drawerForm.validateFields();
      if (isEmptyHtml(editorHtml)) {
        message.error('请输入商品详情');
        return;
      }
      const payload: any = {
        productName: values.productName,
        barcode: values.barcode,
        productIntro: values.productIntro,
        originalPrice: values.originalPrice,
        productStock: values.productStock,
        safeStock: values.safeStock,
        thumbImage: values.thumbImage,
        carouselImages: (values.carouselImages || []).filter((item: string) => item !== ''),
        productDetails: editorHtml,
        status: values.status ?? 1,
      };
      setSubmitLoading(true);
      const res =
        drawerMode === 'edit'
          ? await updateExchangeProductApi(currentShopId, { id: currentId, ...payload })
          : await addExchangeProductApi(currentShopId, payload);
      if (res?.code === 200) {
        message.success(drawerMode === 'edit' ? '编辑成功' : '新建成功');
        setDrawerVisible(false);
        fetchList({ pageNum: 1 });
      }
    } catch (error: any) {
      if (error?.errorFields) return;
      console.error(error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      { title: '名称', dataIndex: 'productName', key: 'productName', width: 160 },
      { title: 'ID', dataIndex: 'id', key: 'id', width: 120 },
      {
        title: '缩略图',
        dataIndex: 'thumbImage',
        key: 'thumbImage',
        width: 120,
        render: (value: string) =>
          value ? (
            <Image
              src={value}
              width={48}
              height={48}
              preview={{ mask: null }}
              style={{ objectFit: 'cover' }}
            />
          ) : (
            <Text type="secondary">--</Text>
          ),
      },
      { title: '仓储条码', dataIndex: 'barcode', key: 'barcode', width: 160 },
      {
        title: '商品原价',
        dataIndex: 'originalPrice',
        key: 'originalPrice',
        width: 140,
        render: (val: string | number) => (val !== undefined && val !== null ? `¥ ${val}` : '--'),
      },
      {
        title: '库存/安全库存',
        key: 'stock',
        width: 200,
        render: (_: any, record: ExchangeProductItem) => {
          const available = record.productStock ?? 0;
          const safe = record.safeStock ?? '--';
          return (
            <Space size={4}>
              <span>{available}</span>
              <span>/</span>
              <span>{safe}</span>
              {checkPermissions(PHYSICAL_ASSET_KEY.UPDATE_STOCK) && (
                <Link onClick={() => openStockModal(record)}>编辑</Link>
              )}
            </Space>
          );
        },
      },
      { title: '创建时间', dataIndex: 'createTime', key: 'createTime', width: 200 },
      {
        title: '操作',
        key: 'action',
        fixed: 'right' as const,
        width: 150,
        render: (_: any, record: ExchangeProductItem) => {
          const editDisabled = !!record.hasOnlineInstance;
          return (
            <Space size={15}>
              {checkPermissions(PHYSICAL_ASSET_KEY.UPDATE) && (
                <Button
                  type="link"
                  style={{ padding: 0 }}
                  disabled={editDisabled}
                  onClick={() => openDrawer('edit', record)}>
                  编辑
                </Button>
              )}
              <Button
                type="link"
                style={{ padding: 0 }}
                onClick={() => openDrawer('copy', record)}>
                复制
              </Button>
              {checkPermissions(PHYSICAL_ASSET_KEY.DELETE) && (
                <Button
                  type="link"
                  style={{ padding: 0 }}
                  danger
                  onClick={() => handleDelete(record)}>
                  删除
                </Button>
              )}
            </Space>
          );
        },
      },
    ],
    [openDrawer],
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 16 }}>实物资产</div>
      <Form
        form={form}
        layout="inline"
        style={{ marginBottom: 16, rowGap: 12 }}>
        <Form.Item
          label="名称"
          name="productName">
          <Input
            placeholder="请输入"
            style={{ width: 200 }}
            allowClear
          />
        </Form.Item>
        <Form.Item
          label="ID"
          name="id">
          <Input
            placeholder="请输入"
            style={{ width: 200 }}
            allowClear
          />
        </Form.Item>
        <Form.Item
          label="仓储条码"
          name="barcode">
          <Input
            placeholder="请输入"
            style={{ width: 200 }}
            allowClear
          />
        </Form.Item>
        <Form.Item
          label="库存状态"
          name="stockStatus">
          <Select
            placeholder="全部"
            allowClear
            style={{ width: 200 }}
            options={stockStatusOptions}
          />
        </Form.Item>
        <Form.Item
          label="创建时间"
          name="createTime">
          <RangePicker
            showTime={{
              defaultValue: [dayjs('00:00:00', 'HH:mm:ss'), dayjs('23:59:59', 'HH:mm:ss')],
            }}
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => fetchList({ pageNum: 1 })}>
              查询
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                form.resetFields();
                fetchList({ pageNum: 1 });
              }}>
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>

      {checkPermissions(PHYSICAL_ASSET_KEY.ADD) && (
        <div style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openDrawer('create')}>
            新建实物资产
          </Button>
        </div>
      )}

      <Table
        rowKey="id"
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        bordered
        pagination={{
          current: pageNum,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: t => `共 ${t} 条`,
          onChange: (page, size) => fetchList({ pageNum: page, pageSize: size }),
        }}
        scroll={{ x: 1100 }}
      />

      <Modal
        title="调整库存"
        open={stockModalVisible}
        onCancel={() => setStockModalVisible(false)}
        onOk={submitStockAdjust}
        okText="提交">
        <Form
          form={stockAdjustForm}
          layout="horizontal"
          labelCol={{ style: { width: 110 } }}>
          <Form.Item label="总库存数量">
            <Text>{stockTarget?.totalStock ?? '--'}</Text>
          </Form.Item>
          <Form.Item label="可分配库存数量">
            <Text>{stockTarget?.availableStock ?? '--'}</Text>
          </Form.Item>
          <Form.Item
            label="调整方式"
            name="mode"
            initialValue="increase"
            rules={[{ required: true, message: '请选择调整方式' }]}>
            <Select
              options={[
                { label: '增加', value: 'increase' },
                { label: '减少', value: 'decrease' },
              ]}
            />
          </Form.Item>
          <Form.Item
            label="调整数量"
            name="quantity"
            rules={[{ required: true, message: '请输入调整数量' }]}>
            <InputNumber
              min={1}
              max={99999999}
              precision={0}
              style={{ width: '100%' }}
              placeholder="请输入"
            />
          </Form.Item>
          <Form.Item
            label="备注"
            name="remark">
            <Input.TextArea
              rows={2}
              placeholder="选填"
            />
          </Form.Item>
          <div style={{ color: '#999', fontSize: 12, lineHeight: '18px' }}>
            注：如需扣减库存，扣件数量不能大于可分配库存数量。
            <br />
            如需扣减数量大于可分配库存，请先下架关联积分兑换权益，或扣减关联权益库存。
          </div>
        </Form>
      </Modal>

      <Drawer
        title={drawerMode === 'edit' ? '编辑实物资产' : '新建实物资产'}
        width={760}
        open={drawerVisible}
        destroyOnHidden
        onClose={() => {
          setDrawerVisible(false);
          if (editor) {
            editor.destroy();
            setEditor(null);
          }
        }}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setDrawerVisible(false)}>取消</Button>
              <Button
                type="primary"
                loading={submitLoading}
                onClick={handleSubmit}>
                保存
              </Button>
            </Space>
          </div>
        }>
        <Form
          form={drawerForm}
          layout="horizontal"
          labelCol={{ style: { width: 100 } }}
          initialValues={{ carouselImages: [''], status: 1, stockUnit: '件' }}>
          <Form.Item
            label="名称"
            name="productName"
            rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="请输入" />
          </Form.Item>
          <Form.Item
            label="仓储条码"
            name="barcode">
            <Input placeholder="请输入" />
          </Form.Item>
          <Form.Item
            label="简介"
            name="productIntro">
            <Input.TextArea
              placeholder="请输入"
              maxLength={200}
              showCount
              rows={3}
            />
          </Form.Item>
          <Form.Item
            label="商品原价"
            name="originalPrice"
            required
            rules={[
              {
                validator: (_, value) => {
                  if (value === undefined || value === null || value === '') {
                    return Promise.reject(new Error('请输入商品原价'));
                  }
                  const valStr = String(value);
                  const decimalCheck = /^\d+(\.\d{1,2})?$/.test(valStr);
                  const valNum = Number(value);
                  if (!Number.isFinite(valNum) || valNum <= 0) {
                    return Promise.reject(new Error('商品原价必须为正数'));
                  }
                  if (!decimalCheck) {
                    return Promise.reject(new Error('最多支持两位小数'));
                  }
                  if (valNum > 99999999) {
                    return Promise.reject(new Error('商品原价不能超过 99999999'));
                  }
                  return Promise.resolve();
                },
              },
            ]}>
            <InputNumber
              min={0.01}
              max={99999999}
              precision={2}
              style={{ width: '100%' }}
              placeholder="请输入"
              addonAfter="元"
            />
          </Form.Item>
          {drawerMode !== 'edit' ? (
            <Form.Item
              label="商品库存"
              name="productStock"
              required
              rules={[
                {
                  validator: (_, value) => {
                    const valNum = Number(value);
                    if (value === undefined || value === null || value === '') {
                      return Promise.reject(new Error('请输入商品库存'));
                    }
                    if (!Number.isInteger(valNum) || valNum <= 0) {
                      return Promise.reject(new Error('商品库存必须为正整数'));
                    }
                    if (valNum > 99999999) {
                      return Promise.reject(new Error('商品库存不能超过 99999999'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}>
              <InputNumber
                min={1}
                max={99999999}
                precision={0}
                style={{ width: '100%' }}
                placeholder="请输入"
              />
            </Form.Item>
          ) : (
            // 编辑时不展示，但保留字段值以传给更新接口
            <Form.Item
              name="productStock"
              hidden>
              <Input />
            </Form.Item>
          )}
          <Form.Item
            label="安全库存"
            name="safeStock"
            required
            rules={[
              {
                validator: (_, value) => {
                  const valNum = Number(value);
                  if (value === undefined || value === null || value === '') {
                    return Promise.reject(new Error('请输入安全库存'));
                  }
                  if (!Number.isInteger(valNum) || valNum <= 0) {
                    return Promise.reject(new Error('安全库存必须为正整数'));
                  }
                  if (valNum > 99999999) {
                    return Promise.reject(new Error('安全库存不能超过 99999999'));
                  }
                  return Promise.resolve();
                },
              },
            ]}>
            <InputNumber
              min={1}
              max={99999999}
              precision={0}
              style={{ width: '100%' }}
              placeholder="请输入"
            />
          </Form.Item>
          <Form.Item
            label="库存单位"
            name="stockUnit">
            <Input placeholder="请输入" />
          </Form.Item>
          <Divider />
          <Form.Item
            label="缩略图"
            required
            name="thumbImage"
            rules={[
              {
                validator: (_, value) => {
                  if (!value) {
                    return Promise.reject(new Error('请上传缩略图'));
                  }
                  return Promise.resolve();
                },
              },
            ]}>
            <ImageUploader
              value={drawerForm.getFieldValue('thumbImage')}
              onChange={val =>
                drawerForm.setFieldsValue({
                  thumbImage: val?.imageUrl || val,
                })
              }
              limitWidth={200}
              limitHeight={200}
              maxSizeKB={5000}
            />
          </Form.Item>
          <Form.Item
            label="轮播图"
            required
            extra={<div style={{ marginTop: 30 }}>最多 5 张</div>}>
            <Form.List
              name="carouselImages"
              rules={[
                {
                  validator: (_, value) => {
                    if (!Array.isArray(value) || !value.length) {
                      return Promise.reject(new Error('请上传轮播图'));
                    }
                    const hasEmpty = value.some(item => !item);
                    if (hasEmpty) {
                      return Promise.reject(new Error('请上传轮播图'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}>
              {(fields, { add, remove }, { errors }) => (
                <>
                  <div style={{ display: 'flex', gap: 16 }}>
                    {fields.map(field => (
                      <>
                        <div
                          key={field.key}
                          style={{
                            width: 100,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            rowGap: 8,
                          }}>
                          <Form.Item
                            {...field}
                            rules={[{ required: false }]}
                            style={{ marginBottom: 0 }}>
                            <ImageUploader
                              value={drawerForm.getFieldValue(['carouselImages', field.name])}
                              onChange={val =>
                                drawerForm.setFieldsValue({
                                  carouselImages: drawerForm
                                    .getFieldValue('carouselImages')
                                    ?.map((item: any, idx: number) =>
                                      idx === field.name ? val?.imageUrl || val : item,
                                    ),
                                })
                              }
                              limitWidth={200}
                              limitHeight={200}
                              maxSizeKB={5000}
                            />
                          </Form.Item>
                          {fields.length > 1 && (
                            <Button
                              style={{
                                marginBottom: 20,
                              }}
                              danger
                              onClick={() => remove(field.name)}>
                              删除
                            </Button>
                          )}
                        </div>
                      </>
                    ))}
                    <Button
                      style={{
                        width: 100,
                        height: 40,
                        marginTop: 16,
                      }}
                      type="dashed"
                      onClick={() => add('')}
                      disabled={fields.length >= 5}>
                      新增轮播图
                    </Button>
                  </div>
                  <Form.ErrorList errors={errors} />
                </>
              )}
            </Form.List>
          </Form.Item>
          <Form.Item
            label="商品详情"
            name="productDetails"
            required>
            {drawerVisible ? (
              <div style={{ border: '1px solid #f0f0f0', borderRadius: 4 }}>
                <Toolbar
                  editor={editor}
                  defaultConfig={toolbarConfig}
                  style={{ borderBottom: '1px solid #f0f0f0' }}
                />
                <Editor
                  key={drawerVisible ? 'editor-open' : 'editor-closed'}
                  defaultConfig={editorConfig}
                  value={editorHtml}
                  onCreated={setEditor}
                  onChange={ed => setEditorHtml(ed.getHtml())}
                  style={{ minHeight: 220, maxHeight: 400, overflowY: 'auto', padding: 12 }}
                />
              </div>
            ) : null}
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default AssetManageGoods;
